import { reaction } from 'mobx';
import type { IReactionDisposer } from 'mobx';

import type { MetagamesCurrentEvent } from '../../dto/metagames.types';
import { getErrorMessage } from '../../utils/errors';
import { devError } from '../../utils/log';
import type { CoinType } from '../cointype/CoinTypeStore';
import type { LaunchGameData } from '../game/types';
import type { RootStore } from '../RootStore';

import type { WarmPreloadGame } from './MetagamesWarmStore';

/**
 * How long a prelaunched metagame open is trusted. The access token in it is a 24h JWT, but when the
 * server-side exchange missed, game-ui exchanges the launch token itself and that one's lifetime is
 * game-provider's — so stay well short of it and let an older one fall back to a live launch.
 */
export const WARM_PRELAUNCH_TTL_MS = 5 * 60 * 1000;

/** Sports launches with every coin at once. */
export type PrelaunchCoin = CoinType | 'ALL';

interface WarmPrelaunchOptions {
  /** Whether a launch is worth handing to an open. Default: it carries the metagames access token. */
  isUsable?: (launchGameData: LaunchGameData) => boolean;
  ttlMs?: number;
}

type PrelaunchEntry<C extends PrelaunchCoin> = {
  gameId: number;
  coin: C;
  startedAt: number;
  request: Promise<LaunchGameData | undefined>;
};

/**
 * Without an access token game-ui would have to exchange the launch token itself, which is the slow path
 * prelaunching avoids.
 */
const hasMetagamesAccessToken = (launchGameData: LaunchGameData) => !!launchGameData.metagames?.accessToken;

/**
 * One game's launch-game, run before the click. A warm open otherwise waits on it before game-ui can
 * fetch anything (prod p50 0.3s, p90 0.9s, p99 5.7s); with it the click is a postMessage.
 *
 * Each prelaunch is handed to one open only — every open still gets its own launch, as before — and any
 * miss (none started, stale, other game or coin, failed, unusable) leaves the open to launch live.
 */
export class WarmPrelaunch<C extends PrelaunchCoin = CoinType> {
  private entry: PrelaunchEntry<C> | null = null;

  private readonly isUsable: (launchGameData: LaunchGameData) => boolean;

  private readonly ttlMs: number;

  constructor(
    private readonly name: string,
    private readonly launch: (coin: C, gameId: number) => Promise<LaunchGameData | undefined>,
    options: WarmPrelaunchOptions = {},
  ) {
    this.isUsable = options.isUsable ?? hasMetagamesAccessToken;
    this.ttlMs = options.ttlMs ?? WARM_PRELAUNCH_TTL_MS;
  }

  public start(gameId: number, coin: C) {
    if (this.isFresh(gameId, coin)) {
      return;
    }

    this.entry = {
      gameId,
      coin,
      startedAt: Date.now(),
      // A failed prelaunch is not an error the player sees: the open just launches live instead.
      request: this.launch(coin, gameId).catch((error) => {
        devError(`${this.name}: prelaunch failed, the next open launches live`, getErrorMessage(error));

        return undefined;
      }),
    };
  }

  public async take(gameId: number, coin: C) {
    if (!this.entry || !this.isFresh(gameId, coin)) {
      return undefined;
    }

    const { request } = this.entry;
    this.entry = null;

    const launchGameData = await request;

    return launchGameData && this.isUsable(launchGameData) ? launchGameData : undefined;
  }

  public clear() {
    this.entry = null;
  }

  private isFresh(gameId: number, coin: C) {
    const { entry } = this;

    return !!entry && entry.gameId === gameId && entry.coin === coin && Date.now() - entry.startedAt < this.ttlMs;
  }
}

export const isEventLive = (event: { endDate: string | Date } | undefined) =>
  !!event && new Date(event.endDate).getTime() > Date.now();

type PrelaunchTarget<C extends PrelaunchCoin> = { gameId: number; coin: C };

interface WatchWarmFrameOptions<C extends PrelaunchCoin> {
  rootStore: RootStore;
  isEventLive: () => boolean;
  /** The open to prelaunch, or null when the warm frame would not take one. */
  getPrelaunchTarget: () => PrelaunchTarget<C> | null;
  prelaunch: WarmPrelaunch<C>;
  preloadGame?: WarmPreloadGame;
}

/**
 * Wires a game to the warm frame while its event is live: asks the frame to preload the game's assets,
 * and prelaunches its next open whenever the frame is ready to take it. Re-runs on every close, when the
 * frame goes back to ready, so the next open is covered too.
 *
 * Call from the store's constructor. The event only arrives after construction, which is what lets the
 * reactions read metagamesWarmStore, built later in RootStore — so both getters must check the event first.
 */
export function watchWarmFrame<C extends PrelaunchCoin>({
  rootStore,
  isEventLive: isLive,
  getPrelaunchTarget,
  prelaunch,
  preloadGame,
}: WatchWarmFrameOptions<C>): IReactionDisposer[] {
  const disposers: IReactionDisposer[] = [];

  if (preloadGame) {
    disposers.push(
      reaction(isLive, (live) => {
        if (live) {
          rootStore.metagamesWarmStore.preload(preloadGame);
        } else {
          rootStore.metagamesWarmStore.cancelPreload(preloadGame);
        }
      }),
    );
  }

  disposers.push(
    reaction(
      () => (isLive() && rootStore.metagamesWarmStore.state === 'ready' ? getPrelaunchTarget() : null),
      (target) => {
        if (target) {
          prelaunch.start(target.gameId, target.coin);
        }
      },
      { equals: (a, b) => a?.gameId === b?.gameId && a?.coin === b?.coin },
    ),
  );

  return disposers;
}

interface WatchWarmMetagameOptions {
  rootStore: RootStore;
  getEvent: () => MetagamesCurrentEvent | undefined;
  prelaunch: WarmPrelaunch;
  preloadGame?: WarmPreloadGame;
}

/**
 * watchWarmFrame for a metagame event. Only an event the player opted into is prelaunched: the warm
 * frame refuses an open before opt-in, and that open goes to the pre-message dialog instead.
 */
export function watchWarmMetagame({ rootStore, getEvent, prelaunch, preloadGame }: WatchWarmMetagameOptions) {
  return watchWarmFrame({
    rootStore,
    prelaunch,
    preloadGame,
    isEventLive: () => isEventLive(getEvent()),
    getPrelaunchTarget: () => {
      const { coinType } = rootStore.coinTypeStore;
      const eventByCoin = getEvent()?.[coinType.toLowerCase() as 'gc' | 'sc'];

      return eventByCoin?.isOptedIn ? { gameId: eventByCoin.gameId, coin: coinType } : null;
    },
  });
}
